'use client';

import type { ReactNode } from 'react';
import { useQueryState } from 'nuqs';
import { cn } from '@/lib/utils';

export interface PageTab {
  id: string;
  label: string;
  /** Optional leading icon (e.g. a Phosphor icon node). */
  icon?: ReactNode;
}

/**
 * Active tab id read from the URL (?<param>=). Falls back to the first tab when
 * the param is absent or points to a tab that isn't currently available — so a
 * page and its <PageTabs> always agree on the active tab from a single source
 * (the URL), with no shared module state.
 */
export function useActiveTab(tabs: PageTab[], param = 'view'): string {
  const [raw] = useQueryState(param);
  return tabs.find((t) => t.id === raw)?.id ?? tabs[0]?.id ?? '';
}

interface PageTabsProps {
  tabs: PageTab[];
  /** URL search param that holds the active tab id. Defaults to "view". */
  param?: string;
  /**
   * Page-level control rendered at the right end of the tab row, sharing its
   * baseline and divider. For controls that scope the whole page rather than
   * one tab; anything tab-specific belongs inside that tab.
   */
  actions?: ReactNode;
  className?: string;
}

/**
 * Top-of-page tab switcher. Owns the URL param (?<param>=) so the active tab is
 * shareable and survives refresh; pages read the same value via useActiveTab.
 * Pages own their own tab list (and any gating), so this stays purely
 * presentational. Renders nothing when there's one tab or fewer and no actions.
 */
export function PageTabs({
  tabs,
  param = 'view',
  actions,
  className,
}: PageTabsProps) {
  const [, setActive] = useQueryState(param);
  const active = useActiveTab(tabs, param);

  if (tabs.length <= 1 && !actions) return null;

  const handleChange = (id: string) => {
    setActive(id);
  };

  return (
    // Full-bleed bottom divider; the tab row is padded inside so the line runs
    // edge-to-edge while the tabs sit on the page gutter.
    <div className={cn('border-b border-ds-border-subtlest', className)}>
      <div className="flex items-center px-6 pt-2">
        {tabs.map(({ id, label, icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => handleChange(id)}
            className={cn(
              '-mb-px flex items-center p-1 border-b border-solid transition-colors',
              isActive ? 'border-ds-brand-subtle' : 'border-transparent'
            )}
          >
            <span
              className={cn(
                'flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 ds-text-body-sm-medium transition-colors',
                isActive
                  ? 'bg-ds-brand-subtlest text-ds-brand-subtle'
                  : 'text-ds-text-subtle hover:text-ds-text-darker'
              )}
            >
              {icon}
              {label}
            </span>
          </button>
          );
        })}
        {actions ? <div className="ml-auto">{actions}</div> : null}
      </div>
    </div>
  );
}
